import * as React from 'react'
import Dropzone from 'react-dropzone'

import { Box, Button, Text, useClassNames } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import TextArea from 'modules/core/components/TextArea'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import ComplaintMessage from 'modules/complaints/store/models/ComplaintMessage'

import { useComplaint } from '../../complaintContext'
import * as styles from './index.vanilla.css'

const FileList = ({ files }: { files: File[] }) => {
  if (files.length === 0) return null

  return (
    <Box gap="s4">
      {files.map((file) => (
        <Box key={file.name}>
          <Text.Normal size="200" weight="semibold" ellipsis>
            {file.name}
          </Text.Normal>
        </Box>
      ))}
    </Box>
  )
}

const FilesButton = ({
  files,
  setFiles,
  disabled,
}: {
  files: File[]
  setFiles: (files: File[]) => void
  disabled: boolean
}) => {
  const cx = useClassNames(styles)

  if (files.length === 0) {
    return (
      <Dropzone onDrop={setFiles} disabled={disabled}>
        {({ getRootProps, getInputProps }) => (
          <div {...getRootProps()} className={cx('picker')}>
            <input {...getInputProps()} />
            <Button type="secondary" disabled={disabled} onClick={() => null} iconName="add">
              {i18n.t('complaints.activity.add_documents')}
            </Button>
          </div>
        )}
      </Dropzone>
    )
  }

  const removeFiles = () => setFiles([])

  return (
    <Button onClick={removeFiles} type="secondary" disabled={disabled} iconName="remove">
      {i18n.t('complaints.activity.remove_documents')}
    </Button>
  )
}

const getDefaultAttributes = (complaintId: number, userId: number) => ({
  text: '',
  files: [],
  complaint_id: complaintId,
  created_at: new Date().toISOString(),
  is_company_message: true,
  access_id: userId,
})

const NewMessage = () => {
  const { complaint, fetch, complaintMessages } = useComplaint()

  if (!complaint) throw new Error('Complaint not found')

  const {
    access: { id: userId },
  } = useCurrentSession()
  const newMessage = React.useRef(
    new ComplaintMessage(getDefaultAttributes(complaint.attributes.id, userId))
  ).current

  const setMessage = (value: string) =>
    newMessage.set({
      text: value,
    })

  const setFiles = (files: File[]) =>
    newMessage.set({
      files,
    })

  const { running: loading, performAction } = useAsyncAction(async () => {
    await complaintMessages.create(newMessage.attributes)
    await fetch()
    newMessage.reset()
  })
  React.useEffect(() => {
    if (complaint.blocked) {
      setMessage('')
      setFiles([])
    }
  }, [complaint.blocked]) // eslint-disable-line react-hooks/exhaustive-deps

  const text = newMessage.attributes.text
  const files = newMessage.attributes.files
  const disabled = loading || complaint.blocked
  const buttonDisabled = text.length === 0 || loading

  return (
    <Box
      background="grey100"
      padding="s24"
      border={{
        all: {
          color: 'grey400',
          width: 's1',
          style: 'solid',
        },
      }}
      borderRadius={{
        all: 'abs004',
      }}
    >
      <Box gap="s24">
        <TextArea
          value={text}
          onChange={setMessage}
          placeholder={i18n.t('complaints.activity.placeholder')}
          readonly={complaint.blocked}
        />
        <FileList files={files} />
        <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
          <FilesButton files={files} setFiles={setFiles} disabled={disabled} />
          <Button onClick={performAction} disabled={buttonDisabled} type="negative">
            {i18n.t('complaints.activity.post')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default observer(NewMessage)
